import React, { CSSProperties, FC, useEffect, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'

import { useFixedDraggable } from './FixedDraggableProvider'

type DraggableCacheProps = {
  left: string
  top: string
}

const storeDraggablePosition = (props: DraggableCacheProps) => {
  window.localStorage.setItem('fixed-draggable-position', JSON.stringify(props))
}

const getDraggablePosition = (): DraggableCacheProps | undefined => {
  const cached = window.localStorage.getItem('fixed-draggable-position')

  if (cached) {
    return JSON.parse(cached)
  }
}

type Props = {
  stickyContainerId?: string
  title: string
  enableOpacitySlider?: boolean
  toolbar?: React.ReactNode
  children?: React.ReactNode
}

export const FixedDraggable: FC<Props> = ({ stickyContainerId = 'rood-fixed-draggable', title, toolbar, children }) => {
  const { isShow, setIsShow, setReference } = useFixedDraggable()
  const ref = useRef<HTMLDivElement | null>(null)
  const container = useRef(document.getElementById(stickyContainerId)).current
  const position = getDraggablePosition()

  const onTouchStart = useMemo<React.TouchEventHandler<HTMLDivElement> | undefined>(() => {
    var pos1 = 0,
      pos2 = 0,
      pos3 = 0,
      pos4 = 0

    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault()
      const touch = e.touches[0] || e.changedTouches[0]

      pos1 = pos3 - touch.pageX
      pos2 = pos4 - touch.pageY
      pos3 = touch.pageX
      pos4 = touch.pageY

      ref.current!.style.top = ref.current!.offsetTop - pos2 + 'px'
      ref.current!.style.left = ref.current!.offsetLeft - pos1 + 'px'
    }

    const onTouchEnd = (e: TouchEvent) => {
      document.body.classList.remove('position-relative')
      document.body.classList.remove('overflow-hidden')

      document.ontouchend = null
      document.ontouchmove = null

      storeDraggablePosition({
        left: ref.current!.offsetLeft - pos1 + 'px',
        top: ref.current!.offsetTop - pos2 + 'px',
      })
    }

    return (e: React.TouchEvent<Element>) => {
      e.preventDefault()
      e.stopPropagation()

      document.body.classList.add('position-relative')
      document.body.classList.add('overflow-hidden')

      const touch = e.touches[0] || e.changedTouches[0]

      pos3 = touch.pageX
      pos4 = touch.pageY

      document.ontouchend = onTouchEnd
      document.ontouchmove = onTouchMove
    }

    // eslint-disable-next-line
  }, [])

  const onMouseDown = useMemo<React.MouseEventHandler<HTMLDivElement> | undefined>(() => {
    var pos1 = 0,
      pos2 = 0,
      pos3 = 0,
      pos4 = 0

    const onMouseMove = (e: MouseEvent) => {
      e.preventDefault()

      pos1 = pos3 - e.clientX
      pos2 = pos4 - e.clientY
      pos3 = e.clientX
      pos4 = e.clientY

      ref.current!.style.top = ref.current!.offsetTop - pos2 + 'px'
      ref.current!.style.left = ref.current!.offsetLeft - pos1 + 'px'
    }

    const onMouseUp = (e: MouseEvent) => {
      document.onmouseup = null
      document.onmousemove = null

      storeDraggablePosition({
        left: ref.current!.offsetLeft - pos1 + 'px',
        top: ref.current!.offsetTop - pos2 + 'px',
      })
    }

    return (e: React.MouseEvent<Element, MouseEvent>) => {
      e.preventDefault()
      e.stopPropagation()

      pos3 = e.clientX
      pos4 = e.clientY

      document.onmouseup = onMouseUp
      document.onmousemove = onMouseMove
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (ref.current) {
      setReference(ref.current)
    }
  }, [setReference, isShow])

  const handleClose = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsShow(false)
  }

  const style: CSSProperties = {
    pointerEvents: 'auto',
    position: 'sticky',
    zIndex: 100,
    top: position ? position.top : '10%',
    left: position ? position.left : '10%',
    minWidth: '300px',
    width: '470px',
    maxHeight: '95vh',
  }

  if (isShow && container) {
    return createPortal(
      <div
        ref={ref}
        className="card"
        style={style}
      >
        <div
          className="card-header min-h-40px px-3 bg-primary cursor-drag"
          onMouseDown={onMouseDown}
          onTouchStart={onTouchStart}
        >
          <h3 className="card-title fw-medium text-white">{title}</h3>
          <div className="ms-auto d-flex justify-content-end align-items-center py-3">
            {toolbar}
            <button
              type="button"
              onClick={handleClose}
              onMouseDown={(e) => e.stopPropagation()}
              className="ms-2 my-auto rounded-circle d-flex justify-content-center align-items-center w-20px h-20px bg-light border-0"
            >
              <i className="fa fa-xmark fs-7 text-dark"></i>
            </button>
          </div>
        </div>
        <div className="card-body p-5 position-relative">{children}</div>
      </div>,
      container
    )
  }

  return null
}
